import { ScaffoldDropZone } from "../../scaffold";
import { ArsSidebarItemKind } from "../sidebar/interface";
import {
  ARST_INSERT_COLUMN_AFTER,
  ARST_INSERT_COLUMN_BEFORE,
  ARST_INSERT_OPERATION_COLUMN_AFTER,
  ARST_INSERT_OPERATION_COLUMN_BEFORE,
  ARST_INSERT_OPERATION_ROW_AFTER,
  ARST_INSERT_OPERATION_ROW_BEFORE,
  ARST_INSERT_OPERATION_SUBGROUP,
  ARST_INSERT_ROW_AFTER,
  ARST_INSERT_ROW_BEFORE,
  ARST_INSERT_SUBGROUP,
  ARST_REMOVE_GROUPINGFACTOR,
  ARST_REMOVE_OPERATION,
  ARST_TOGGLE_GROUP_LABEL_INLINE,
  ARST_TOGGLE_GROUP_LABEL_VISIBILITY,
  ARST_TOGGLE_INLINE_WITH_PREVIOUS_LEVEL,
  ARST_TOGGLE_SPACE_AFTER_GROUP,
  ARST_TOGGLE_SPACE_AFTER_SUBGROUPS,
  ArsTableCellActionConfig,
  ArsTableCellData,
  ArsTableCellType,
  ArsTableContextMenuActionDefinition,
  ArsTableDropActionDefinition,
  ArsTableState,
} from "./interface";

/**
 * Drop zones on the left edge of a column cell (insert-before).
 */
export const LEFT_COLUMN_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.LeftEdgeBottom,
]);

/**
 * Drop zones on the right edge of a column cell (insert-after).
 */
export const RIGHT_COLUMN_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
]);

/**
 * Drop zones on the top of a column cell (insert row above).
 */
export const TOP_COLUMN_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.TopEdgeLeft,
  ScaffoldDropZone.TopEdgeRight,
  ScaffoldDropZone.TopRight,
]);

/**
 * Drop zones on the bottom of a column cell (insert row below).
 */
export const BOTTOM_COLUMN_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Drop zones on the top edge of a row cell (insert row above).
 */
export const TOP_ROW_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.TopEdgeLeft,
  ScaffoldDropZone.TopEdgeRight,
]);

/**
 * Drop zones on the bottom edge of a row cell (insert row below).
 */
export const BOTTOM_ROW_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
]);

/**
 * Drop zones on the left edge of a row cell (insert column to the left).
 */
export const LEFT_ROW_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.CentreTopLeft,
  ScaffoldDropZone.LeftEdgeBottom,
  ScaffoldDropZone.CentreBottomLeft,
]);

/**
 * Drop zones on the right edge of a row cell (insert column to the right).
 */
export const RIGHT_ROW_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Drop zones for row header cells (insert column)
 */
export const ROWHEADER_COLUMN_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Drop zones for row header cells (insert row)
 */
export const ROWHEADER_ROW_ZONES = new Set<ScaffoldDropZone>([
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
]);

export const removeGroupingFactor: ArsTableContextMenuActionDefinition = {
  label: "Remove group",
  operation: ARST_REMOVE_GROUPINGFACTOR,
};
export const removeOperation: ArsTableContextMenuActionDefinition = {
  label: "Remove operation",
  operation: ARST_REMOVE_OPERATION,
};
export const toggleGroupFactorLabel: ArsTableContextMenuActionDefinition = {
  label: "Toggle visible group label",
  operation: ARST_TOGGLE_GROUP_LABEL_VISIBILITY,
};
export const toggleGroupLabelInline: ArsTableContextMenuActionDefinition = {
  label: "Toggle inline group label",
  operation: ARST_TOGGLE_GROUP_LABEL_INLINE,
};
export const toggleInlineWithPreviousLevel: ArsTableContextMenuActionDefinition = {
  label: "Toggle inline with previous level",
  operation: ARST_TOGGLE_INLINE_WITH_PREVIOUS_LEVEL,
};
export const toggleSpaceAfterGroup: ArsTableContextMenuActionDefinition = {
  label: "Toggle space after group",
  operation: ARST_TOGGLE_SPACE_AFTER_GROUP,
};
export const toggleSpaceAfterSubGroups: ArsTableContextMenuActionDefinition = {
  label: "Toggle space after each subgroup",
  operation: ARST_TOGGLE_SPACE_AFTER_SUBGROUPS,
};

const columnDropActions: ArsTableDropActionDefinition[] = [
  {
    droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
    dropZones: LEFT_COLUMN_ZONES,
    operation: ARST_INSERT_COLUMN_BEFORE,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
    dropZones: RIGHT_COLUMN_ZONES,
    operation: ARST_INSERT_COLUMN_AFTER,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
    dropZones: BOTTOM_COLUMN_ZONES,
    operation: ARST_INSERT_SUBGROUP,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
    dropZones: LEFT_COLUMN_ZONES,
    operation: ARST_INSERT_OPERATION_COLUMN_BEFORE,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
    dropZones: RIGHT_COLUMN_ZONES,
    operation: ARST_INSERT_OPERATION_COLUMN_AFTER,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
    dropZones: BOTTOM_COLUMN_ZONES,
    operation: ARST_INSERT_OPERATION_SUBGROUP,
  },
];

const rowDropActions: ArsTableDropActionDefinition[] = [
  {
    droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
    dropZones: TOP_ROW_ZONES,
    operation: ARST_INSERT_ROW_BEFORE,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
    dropZones: BOTTOM_ROW_ZONES,
    operation: ARST_INSERT_ROW_AFTER,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
    dropZones: RIGHT_ROW_ZONES,
    operation: ARST_INSERT_SUBGROUP,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
    dropZones: TOP_ROW_ZONES,
    operation: ARST_INSERT_OPERATION_ROW_BEFORE,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
    dropZones: BOTTOM_ROW_ZONES,
    operation: ARST_INSERT_OPERATION_ROW_AFTER,
  },
  {
    droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
    dropZones: RIGHT_ROW_ZONES,
    operation: ARST_INSERT_OPERATION_SUBGROUP,
  },
];

/**
 * Create action configuration for a given cell
 *
 * @param value - The value of the cell for which the action configuration is being generated
 * @param state - The current state of the ArsTable
 * @returns The action configuration for the given cell
 */
export const cellActionConfig: <T>(
  value: ArsTableCellData<T>,
  state: ArsTableState<T> | null,
) => ArsTableCellActionConfig = (value, state) => {
  if (!state)
    return {
      contextMenuActions: [],
      dropActions: [],
    };

  switch (value.cellType) {
    case ArsTableCellType.ColumnGroupCell:
    case ArsTableCellType.ColumnGroupLabel:
      return {
        contextMenuActions: [toggleGroupFactorLabel, removeGroupingFactor],
        dropActions: columnDropActions,
      };
    case ArsTableCellType.RowGroupCell:
    case ArsTableCellType.RowGroupLabel: {
      const ret = {
        contextMenuActions: [
          toggleGroupFactorLabel,
          removeGroupingFactor,
          toggleGroupLabelInline,
          toggleSpaceAfterGroup,
        ],
        dropActions: rowDropActions,
      };
      // Analysis methods have no groups or data values, so cannot space their sub-groups
      if (value.targetType !== "analysisMethod")
        ret.contextMenuActions.push(toggleSpaceAfterSubGroups);
      if (value.dataPath.split("/").length > 3)
        ret.contextMenuActions.push(toggleInlineWithPreviousLevel);
      return ret;
    }
    case ArsTableCellType.ColumnOperation:
      return {
        contextMenuActions: [removeOperation],
        dropActions: columnDropActions,
      };
    case ArsTableCellType.RowOperation:
      return {
        contextMenuActions: [removeOperation, toggleSpaceAfterGroup],
        dropActions: rowDropActions,
      };

    case ArsTableCellType.RowHeader:
      return {
        contextMenuActions: [],
        dropActions: [
          {
            droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
            dropZones: ROWHEADER_COLUMN_ZONES,
            operation: ARST_INSERT_COLUMN_AFTER,
          },
          {
            droppedDataKinds: [ArsSidebarItemKind.GroupingFactor],
            dropZones: ROWHEADER_ROW_ZONES,
            operation: ARST_INSERT_ROW_AFTER,
          },
          {
            droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
            dropZones: ROWHEADER_COLUMN_ZONES,
            operation: ARST_INSERT_OPERATION_COLUMN_AFTER,
          },
          {
            droppedDataKinds: [ArsSidebarItemKind.Operation, ArsSidebarItemKind.AnalysisMethod],
            dropZones: ROWHEADER_ROW_ZONES,
            operation: ARST_INSERT_OPERATION_ROW_AFTER,
          },
        ],
      };
    default:
      return {
        contextMenuActions: [],
        dropActions: [],
      };
  }
};
